/**
 * The rich view of a run's `DisplayPayload` — `bimgo-ai-web`'s
 * `ExecutionDisplayView.tsx`: summary, metrics, a "Data Tables (n)" button
 * that opens the tables in a tabbed dialog, warnings, and an optional "View
 * full result" action. The payload is what the agent's `buildDisplayPayload`
 * produces for both the chat tool and the execute route.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { Modal } from "../settings/Modal";

export interface DisplayMetric {
  label: string;
  value: string;
}
export interface DisplayTable {
  title?: string;
  columns: string[];
  rows: Record<string, unknown>[];
}
export interface DisplayPayload {
  title?: string;
  summary?: string[];
  metrics?: DisplayMetric[];
  tables?: DisplayTable[];
  warnings?: string[];
  nextActions?: string[];
}

export function isDisplayPayload(value: unknown): value is DisplayPayload {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function ExecutionDisplayView({ display, onViewRawResult }: { display: DisplayPayload; onViewRawResult?: () => void }) {
  const { t } = useLocale();
  const [tablesOpen, setTablesOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const tables = display.tables ?? [];
  const hasTables = tables.length > 0;
  const hasDetails = hasTables || !!onViewRawResult;

  return (
    <div className="exec-display">
      {display.summary && display.summary.length > 0 ? (
        <section className="exec-block">
          <h5>{t("toolEditor.summary")}</h5>
          <p className="exec-summary">{display.summary.join(" ")}</p>
        </section>
      ) : null}

      {display.metrics && display.metrics.length > 0 ? (
        <div className="exec-metrics">
          {display.metrics.map((metric, i) => (
            <section className="exec-block" key={`metric-${i}`}>
              <h5>{metric.label}</h5>
              <p className="exec-metric-value">{metric.value}</p>
            </section>
          ))}
        </div>
      ) : null}

      {hasDetails ? (
        <section className="exec-block">
          <h5>{t("toolEditor.resultDetails")}</h5>
          <div className="exec-actions">
            {hasTables ? (
              <button type="button" className="exec-btn is-tables" onClick={() => setTablesOpen(true)}>
                {t("toolEditor.dataTables")} ({tables.length})
              </button>
            ) : null}
            {onViewRawResult ? (
              <button type="button" className="exec-btn is-raw" onClick={onViewRawResult}>
                {t("skillHistory.viewResult")}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {display.warnings && display.warnings.length > 0 ? (
        <section className="exec-warnings">
          <h5>{t("toolEditor.warnings")}</h5>
          <ul>
            {display.warnings.map((w, i) => (
              <li key={`warning-${i}`}>{w}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasTables ? (
        <Modal open={tablesOpen} onClose={() => setTablesOpen(false)} title={t("toolEditor.dataTables")} size="5xl">
          <div className="skill-seg-tabs exec-tabs" role="tablist">
            {tables.map((table, i) => (
              <button
                key={`tab-${i}`}
                type="button"
                role="tab"
                aria-selected={tab === i}
                className={`skill-seg-tab${tab === i ? " is-active" : ""}`}
                onClick={() => setTab(i)}
              >
                {table.title || `Table ${i + 1}`}
              </button>
            ))}
          </div>
          {tables[tab] ? (
            <div className="exec-table-wrap">
              <table className="exec-table">
                <thead>
                  <tr>
                    {tables[tab].columns.map((c, i) => (
                      <th key={`col-${i}`}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tables[tab].rows.map((row, r) => (
                    <tr key={`row-${r}`}>
                      {tables[tab]!.columns.map((c, i) => (
                        <td key={`cell-${r}-${i}`}>{formatDisplayValue(row[c])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
